import { useEffect, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { extractUniqueKeys } from 'redux/actions/reports';
import { handleTypeName, objEnum } from 'pages/Report/utils';
import { convertCamelCaseToTitleCase } from 'helpers/convertCamelCaseToTitleCase';

export const ReportType = {
  SalesByDate: 'Sales by Date',
  DetailedSalesByProvider: 'Detailed Sales by Provider',
  SalesByHour: 'Sales by Hour',
  DetailedOrderReport: 'Detailed Order Report',
  ChargebackDisputeReport: 'Chargeback/Dispute Report',
};

export const REPORT_TYPE_FOR_TOTAL_REPORT = Object.values(ReportType);

export const ReportFilterType = {
  contains: 'contains',
  equals: 'equals',
  starts_with: 'startsWith',
  ends_with: 'endsWith',
  is_empty: 'isEmpty',
  is_not_empty: 'isNotEmpty',
}

export const DETAILED_ORDER_REPORT_FIELDS = [
  'Brand Name',
  'Location Name',
  'Address1',
  'Address2',
  'City',
  'State',
  'Zip Code',
  'Order Number',
  'Be Ready Time',
  'Employee Id',
  'Employee Name',
  'Employee Last Name',
  'Employee Initials',
];

export const styles= {
  my: 2,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: (theme) => theme.palette.primary.main,
    color: 'white',
  },
  '& .MuiDataGrid-row	': {
    '&:nth-of-type(odd)': {
      backgroundColor: (theme) => theme.palette.action.hover,
    },
  },
  '& .MuiDataGrid-sortIcon': {
    color: 'white',
  },
  '& .MuiDataGrid-filterIcon': {
    color: 'white',
  },
  '& .MuiDataGrid-menuIcon	': {
    '& .MuiButtonBase-root': { color: 'white' },
  },
  '& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
    margin: 0,
  },
}

export const numberFields = [
  'total',
  'sub_total',
  'tax',
  'tip',
  'delivery',
  'total_sales',
  'total_amount',
  'average_sale',
  'total_tax',
  'total_tips',
  'total_discounts',
  'subtotal_sales',
  'total_delivery',
  'driver_tip',
  'kitchen_tip',
  'total_driver_tips',
  'total_driver_tip',
  'total_kitchen_tips',
  'total_kitchen_tip',
  'total_coupon',
  'coupon',
  'total_delivery_fee',
];

export const numberFieldsTotals = [
  'total',
  'sub_total',
  'tax',
  'tip',
  'delivery',
  'total_sales',
  'total_amount',
  'average_sale',
  'total_tax',
  'total_tips',
  'total_discounts',
  'subtotal_sales',
  'total_delivery',
  'driver_tip',
  'kitchen_tip',
  'total_driver_tips',
  'total_driver_tip',
  'total_kitchen_tips',
  'total_kitchen_tip',
  'total_coupon',
  'coupon',
  'total_delivery_fee',
  'total_orders',
  'rewards',
  'refundTotal',
  'orderTotal',
  'chargeTotal',
  'total_pickups_qty',
  'total_delivery_qty'
];

export const numberFieldsWithoutPrice = [
  'total_orders',
  'refundTotal',
  'orderTotal',
  'chargeTotal',
  'total_pickups_qty',
  'total_delivery_qty'
]


export const longer_names = [
  'brand_id',
  'business_id',
  'location_id',
  'address'
];

const hideFields = [
  'Srv'
]


const parseNumber = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/[^0-9.-]+/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const useTableReport = (reportListData, headerKeys, currentReport, tableTypeName) => {
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState(100);
  const [hasFilterData, setHasFilterData] = useState([]);

  useEffect(() => {
    if (reportListData?.length > 0) {
      setHasFilterData(reportListData);
    }
  }, [reportListData]);

  const tableType = currentReport?.type || tableTypeName;
  const tableTypeUp = tableType?.toUpperCase() || '';

  const transformTableType = t(handleTypeName(tableTypeUp));

  const handelRowData = (reportList, headerKeysData) => {
    let headers = [];
    let rows = [...reportList]?.sort((a, b) => {
      return parseNumber(b.total_amount) - parseNumber(a.total_amount);
    }) || [];
    if (Array.isArray(headerKeysData) && !headerKeysData?.length && rows && rows[0]) {
      headerKeysData = extractUniqueKeys(rows);
    }

    for (let hName of headerKeysData) {
      const headerName = convertCamelCaseToTitleCase(hName.replaceAll('_', ' '));
      const minWidth = 
        (transformTableType === t(`${objEnum.SALES_BY_PROVIDER}`) ||  
        transformTableType === t(`${objEnum.SUMMARY}`))
        && longer_names.includes(hName);
        
      headers.push({
        field: hName,
        headerName: t(headerName),
        minWidth: minWidth ? 200 : undefined,
        filterable: false,
        ...(numberFields.includes(hName) && {
          sortComparator: (v1, v2) => parseNumber(v1) - parseNumber(v2),
        }),
        hide: Boolean(
          (transformTableType === t(`${objEnum.DETAILED_ORDER_REPORT}`) &&
          DETAILED_ORDER_REPORT_FIELDS.includes(headerName)) ||
          (transformTableType === t(`${objEnum.SALES_BY_PROVIDER}`) &&
          hideFields.includes(headerName))
        ),
        flex: 1,
        renderCell: ({ value, field }) => {
          if (field === 'reason') {
            return (
              <Typography
                fontSize="14px"
                className={'report--vertical'}
                textAlign={numberFields.includes(field) ? 'right' : 'left'}
              >
                {value}
              </Typography>
            );
          } else {
            return (
              <Typography fontSize="14px" textAlign={numberFields.includes(field) ? 'right' : 'left'} width="100%">
                {value}
              </Typography>
            );
          }
        },
      });
    }
    return { headers, rows };
  };

  const rowData = useMemo(() => {
    const currentList = hasFilterData?.length > 0 ? hasFilterData : reportListData;
    return handelRowData(currentList, headerKeys);
  }, [reportListData, headerKeys, hasFilterData]);

  const totals = useMemo(() => {
    let totalsObj = {};
    const currentList = hasFilterData.length > 0 ? hasFilterData : reportListData;
    numberFieldsTotals.forEach((field) => {
      totalsObj[field] = currentList.reduce((sum, row) => {
        const value = parseNumber(row[field]);
        return sum + (isNaN(value) ? 0 : value);
      }, 0);
    });
    return totalsObj;
  }, [hasFilterData, reportListData, numberFields]);

  const handleFilterChange = (newFilterModel) => {
    const { items } = newFilterModel;

    const applyFilter = (row) => {
      return items.every((filter) => {
        const { columnField, operatorValue, value } = filter;
        const fieldValue = row[columnField]?.toString()?.toLowerCase();

        switch (operatorValue) {
          case ReportFilterType.contains:
            return fieldValue?.includes(value?.toLowerCase());
          case ReportFilterType.equals:
            return fieldValue === value?.toLowerCase();
          case ReportFilterType.starts_with:
            return fieldValue?.startsWith(value?.toLowerCase());
          case ReportFilterType.ends_with:
            return fieldValue?.endsWith(value?.toLowerCase());
          case ReportFilterType.is_empty:
            return !fieldValue;
          case ReportFilterType.is_not_empty:
            return !!fieldValue;
          default:
            return true;
        }
      });
    };

    const result = rows.filter(applyFilter);
    setHasFilterData(result || []);
  };

  const { headers, rows } = rowData;

  return {
    pageSize,
    setPageSize,
    transformTableType,
    headers,
    rows,
    totals,
    handleFilterChange,
  }
}

export default useTableReport;